import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtTime, fmtDate } from "@/lib/caesar";
import { PageHeader, Loading } from "@/components/common";
import { CaesarFive, ListCard, MutedEmpty, ActionBtn } from "@/components/widgets";
import { toast } from "sonner";
import { Check } from "lucide-react";

export default function Today() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [projectMap, setProjectMap] = useState({});

  const load = useCallback(async () => {
    const [t, projs] = await Promise.all([api.get("/dashboard/today"), api.get("/projects")]);
    setData(t.data);
    const map = {}; projs.data.forEach((p) => (map[p.id] = p.name)); setProjectMap(map);
  }, []);
  useEffect(() => { load(); }, [load]);

  const completeTask = async (id) => { await api.put(`/tasks/${id}`, { status: "done" }); toast.success("Done"); load(); };

  if (!data) return <Loading label="Loading today…" />;

  return (
    <div className="space-y-8">
      <PageHeader title="Today" subtitle="Tactical execution — what gets done now" testId="today-header" />

      <div>
        <div className="label-caps mb-3">The Five</div>
        <CaesarFive items={data.caesars_five} projectMap={projectMap} onReload={load} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <ListCard title="Today's Calendar" count={data.calendar.length} testId="today-calendar">
          {data.calendar.length === 0 ? <MutedEmpty>No meetings today.</MutedEmpty> :
            data.calendar.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/calendar")}>
                <span className="font-num text-xs text-gold w-16 shrink-0">{fmtTime(e.time_start)}</span>
                <p className="text-sm truncate">{e.title}</p>
              </div>
            ))}
        </ListCard>

        <ListCard title="Tasks" count={data.tasks.length} testId="today-tasks">
          {data.tasks.length === 0 ? <MutedEmpty>No open tasks.</MutedEmpty> :
            data.tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg gap-2">
                <div className="min-w-0"><p className="text-sm truncate">{t.title}</p>{t.deadline && <span className="label-caps text-[0.55rem] text-samber">{fmtDate(t.deadline)}</span>}</div>
                <button data-testid={`today-task-complete-${t.id}`} onClick={() => completeTask(t.id)} className="text-sgreen shrink-0"><Check size={16} /></button>
              </div>
            ))}
        </ListCard>

        <ListCard title="People to Contact" count={data.people_to_contact.length} testId="today-people">
          {data.people_to_contact.length === 0 ? <MutedEmpty>No one to contact today.</MutedEmpty> :
            data.people_to_contact.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/people")}>
                <p className="text-sm truncate">{p.name}</p><span className="label-caps text-[0.55rem]">{p.organization || ""}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Money to Follow Up" count={data.money_followup.length} testId="today-money">
          {data.money_followup.length === 0 ? <MutedEmpty>No money to chase.</MutedEmpty> :
            data.money_followup.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/money")}>
                <div className="min-w-0"><p className="text-sm truncate">{m.party}</p><span className="label-caps text-[0.55rem]">{m.status}</span></div>
                <span className="font-num text-sm text-samber ml-2 shrink-0">{fmtMoney(m.amount)}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Deadlines" count={data.deadlines.length} testId="today-deadlines">
          {data.deadlines.length === 0 ? <MutedEmpty>No deadlines today.</MutedEmpty> :
            data.deadlines.map((t) => (<div key={t.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg"><p className="text-sm">{t.title}</p><span className="label-caps text-[0.55rem] text-samber">{fmtDate(t.deadline)}</span></div>))}
        </ListCard>

        <ListCard title="Waiting On" count={data.waiting_on.length} testId="today-waiting">
          {data.waiting_on.length === 0 ? <MutedEmpty>Not waiting on anyone.</MutedEmpty> :
            data.waiting_on.map((t) => (<div key={t.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg"><p className="text-sm">{t.title}</p><span className="label-caps text-[0.55rem]">{t.owner || "—"}</span></div>))}
        </ListCard>

        <ListCard title="Red Alerts" count={data.red_alerts.length} testId="today-alerts">
          {data.red_alerts.length === 0 ? <MutedEmpty>No red alerts. The Empire is secure.</MutedEmpty> :
            data.red_alerts.map((a) => (
              <div key={a.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/alerts")}>
                <p className="text-sm text-sred">{a.problem}</p>
              </div>
            ))}
        </ListCard>

        <ListCard title="Completed Today" count={data.completed_today.length} testId="today-completed">
          {data.completed_today.length === 0 ? <MutedEmpty>Nothing completed yet — go execute.</MutedEmpty> :
            data.completed_today.map((t) => (<div key={t.id} className="px-3 py-2.5 flex items-center gap-2"><Check size={14} className="text-sgreen" /><p className="text-sm line-through text-[#A3A3A3]">{t.title}</p></div>))}
        </ListCard>
      </div>
    </div>
  );
}
